import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import api from '../services/api';

function AdminClientes() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('fecha_registro');
  const [sortDir, setSortDir] = useState('desc');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [filaHover, setFilaHover] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    cargar();
  }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/clientes');
      setClientes(res.data || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
      setError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null;

  const limpiarTel = (t) => (t || '').toString().replace(/\D/g, '');
  const waLink = (t) => {
    const tel = limpiarTel(t);
    if (!tel) return null;
    const conLada = tel.length === 10 ? `52${tel}` : tel;
    return `https://wa.me/${conLada}`;
  };

  // Precedencia: bloqueada > no validada > validada.
  const getValidacion = (c) => {
    if (c.usuario_id == null) return { label: '—', bg: '#f3f4f6', color: '#6b7280' };
    if (c.bloqueado === 1) return { label: '🔴 Bloqueada', bg: '#fee2e2', color: '#991b1b' };
    if (c.validado !== 1) return { label: '🟡 No validada', bg: '#fef3c7', color: '#92400e' };
    return { label: '🟢 Validada', bg: '#d1fae5', color: '#065f46' };
  };

  const esPendiente = (c) => c.usuario_id != null && c.bloqueado !== 1 && c.validado !== 1;

  const validarCliente = async (c) => {
    if (!window.confirm(`¿Validar a "${c.razon_social || c.email}"? Podrá generar guías de inmediato.`)) return;
    try {
      await api.post('/admin/validar-usuario', { usuario_id: c.usuario_id });
      // Actualizacion local: el badge cambia al instante, sin recargar la tabla.
      setClientes((prev) => prev.map((x) => x.usuario_id === c.usuario_id ? { ...x, validado: 1 } : x));
    } catch (err) {
      alert('Error al validar: ' + (err.response?.data?.error || err.message));
    }
  };

  const invalidarCliente = async (c) => {
    if (!window.confirm(`¿Quitar la validación a "${c.razon_social || c.email}"? Dejará de poder generar guías.`)) return;
    try {
      await api.post('/admin/invalidar-usuario', { usuario_id: c.usuario_id });
      setClientes((prev) => prev.map((x) => x.usuario_id === c.usuario_id ? { ...x, validado: 0 } : x));
    } catch (err) {
      alert('Error al quitar validación: ' + (err.response?.data?.error || err.message));
    }
  };

  const getEstado = (c) => {
    if (!c.total_envios || c.total_envios === 0) {
      return { label: 'Sin envíos', bg: '#fee2e2', color: '#991b1b' };
    }
    const dias = daysSince(c.ultimo_envio);
    if (dias !== null && dias > 30) {
      return { label: `Inactivo (${dias}d)`, bg: '#fef3c7', color: '#92400e' };
    }
    return { label: 'Activo', bg: '#d1fae5', color: '#065f46' };
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = clientes.filter((c) => {
      if (soloPendientes && !esPendiente(c)) return false;
      if (!q) return true;
      return (
        (c.razon_social || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.telefono || '').toLowerCase().includes(q)
      );
    });
    arr.sort((a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];
      if (sortBy === 'fecha_registro' || sortBy === 'ultimo_envio') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else if (sortBy === 'total_envios') {
        va = Number(va || 0); vb = Number(vb || 0);
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [clientes, search, sortBy, sortDir, soloPendientes]);

  const totalPendientes = clientes.filter(esPendiente).length;

  const sortIcon = (col) => sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fb' }}>
        <div style={{ fontSize: '1.25rem', color: '#ff8c00' }}>Cargando clientes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fb' }}>
        <div style={{ textAlign: 'center', color: '#dc2626' }}>
          <div style={{ marginBottom: '0.5rem' }}>{error}</div>
          <button onClick={cargar} style={{ padding: '0.5rem 1rem', backgroundColor: '#ff8c00', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>Reintentar</button>
        </div>
      </div>
    );
  }

  const th = (col, label, align = 'left') => (
    <th
      onClick={() => handleSort(col)}
      style={{
        padding: '0.75rem 1rem',
        textAlign: align,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
    >
      {label}{sortIcon(col)}
    </th>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fb', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
      <nav style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '3px solid #ff8c00' }}>
        <div style={{ maxWidth: '90rem', margin: '0 auto', padding: isMobile ? '0 0.75rem' : '0 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', height: isMobile ? '3.5rem' : '4rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src="/logo.png" alt="Logo" style={{ height: isMobile ? '5rem' : '10rem' }} />
              <span style={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 600, color: '#ff8c00', backgroundColor: '#fff7ed', padding: '0.25rem 0.75rem', borderRadius: 9999 }}>
                Clientes
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button onClick={() => navigate('/admin/dashboard')} style={navBtn}>Dashboard</button>
              <button onClick={() => navigate('/admin')} style={navBtn}>Panel</button>
              <button onClick={handleLogout} style={{ ...navBtn, backgroundColor: 'transparent', color: '#6b7280' }}>Salir</button>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: '90rem', margin: '0 auto', padding: isMobile ? '1rem 0.75rem' : '1.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <h1 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Seguimiento de clientes
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {filtered.length} de {clientes.length} clientes
          </p>
        </div>

        <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <input
            type="text"
            placeholder="Buscar por nombre, email o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 260px',
              maxWidth: '420px',
              padding: '0.6rem 0.875rem',
              border: '1.5px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '0.95rem',
              outline: 'none',
              backgroundColor: 'white'
            }}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer',
            padding: '0.6rem 0.875rem', borderRadius: '0.5rem', backgroundColor: 'white',
            border: soloPendientes ? '1.5px solid #f59e0b' : '1.5px solid #e5e7eb',
            fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap'
          }}>
            <input
              type="checkbox"
              checked={soloPendientes}
              onChange={(e) => setSoloPendientes(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Solo pendientes de validar
            <span style={{
              backgroundColor: totalPendientes > 0 ? '#fef3c7' : '#f3f4f6',
              color: totalPendientes > 0 ? '#92400e' : '#6b7280',
              borderRadius: 9999, padding: '0.1rem 0.5rem', fontSize: '0.75rem', fontWeight: 700
            }}>{totalPendientes}</span>
          </label>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb',
          overflow: 'auto'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                {th('razon_social', 'Cliente')}
                {th('email', 'Email')}
                {th('telefono', 'Teléfono')}
                {th('fecha_registro', 'Registro')}
                {th('total_envios', 'Envíos', 'center')}
                {th('ultimo_envio', 'Último envío')}
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Validación</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Estado</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const estado = getEstado(c);
                const validacion = getValidacion(c);
                const wa = waLink(c.telefono);
                const sinEnvios = !c.total_envios || c.total_envios === 0;
                const dias = daysSince(c.ultimo_envio);
                const inactivo = dias !== null && dias > 30;
                const rowBg = sinEnvios ? '#fef2f2' : (inactivo ? '#fffbeb' : 'white');
                return (
                  <tr
                    key={c.empresa_id}
                    onMouseEnter={() => setFilaHover(c.empresa_id)}
                    onMouseLeave={() => setFilaHover(null)}
                    style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: rowBg }}
                  >
                    <td style={td}><strong>{c.razon_social || '—'}</strong></td>
                    <td style={td}>{c.email || '—'}</td>
                    <td style={td}>{c.telefono || '—'}</td>
                    <td style={td}>{formatDate(c.fecha_registro)}</td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{c.total_envios || 0}</td>
                    <td style={td}>{c.ultimo_envio ? `${formatDate(c.ultimo_envio)} (${dias}d)` : '—'}</td>
                    <td style={td}>
                      <span style={{
                        backgroundColor: validacion.bg, color: validacion.color,
                        padding: '0.2rem 0.6rem', borderRadius: 9999,
                        fontSize: '0.75rem', fontWeight: 600
                      }}>{validacion.label}</span>
                    </td>
                    <td style={td}>
                      <span style={{
                        backgroundColor: estado.bg, color: estado.color,
                        padding: '0.2rem 0.6rem', borderRadius: 9999,
                        fontSize: '0.75rem', fontWeight: 600
                      }}>{estado.label}</span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                      {wa ? (
                        <a
                          href={wa}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '0.35rem 0.75rem',
                            backgroundColor: '#25d366',
                            color: 'white',
                            borderRadius: '0.375rem',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textDecoration: 'none'
                          }}
                        >WhatsApp</a>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Sin tel</span>
                      )}
                      {esPendiente(c) && (
                        <button onClick={() => validarCliente(c)} style={{
                          padding: '0.35rem 0.75rem', backgroundColor: '#ff8c00', color: 'white',
                          border: 'none', borderRadius: '0.375rem', fontSize: '0.8rem',
                          fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                        }}>Validar</button>
                      )}
                      {c.usuario_id != null && c.bloqueado !== 1 && c.validado === 1 && (
                        // Accion rara (revertir un error): oculta hasta pasar el mouse, para no
                        // invitar al clic accidental. En tactil no hay hover, ahi va visible.
                        <button
                          onClick={() => invalidarCliente(c)}
                          title="Quitar validación"
                          style={{
                            padding: '0.35rem 0.6rem', backgroundColor: 'transparent', color: '#9ca3af',
                            border: '1px solid #e5e7eb', borderRadius: '0.375rem', fontSize: '0.75rem',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            opacity: (isMobile || filaHover === c.empresa_id) ? 1 : 0,
                            pointerEvents: (isMobile || filaHover === c.empresa_id) ? 'auto' : 'none',
                            transition: 'opacity 0.15s ease'
                          }}
                        >Quitar</button>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const navBtn = {
  padding: '0.4rem 0.75rem',
  fontSize: '0.8rem',
  color: '#374151',
  backgroundColor: '#f3f4f6',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer'
};

const td = {
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: '#111827',
  whiteSpace: 'nowrap'
};

export default AdminClientes;
